import React, { useState, useEffect, useRef, useMemo } from "react"
import { MapContainer, TileLayer } from "react-leaflet"
import MarkerClusterGroup from "react-leaflet-cluster"
import "leaflet/dist/leaflet.css"
import styles from "../Dashboard.module.css"
import MapMarker from "./MapMarker"
import { useHistory } from "react-router-dom/cjs/react-router-dom.min"

const Map = ({ locations = [], filters = [], handlePopupOpen, handlePopupClose, focusItemId }) => {
  const mapRef = useRef(null)
  const history = useHistory();

  const [layerType, setLayerType] = useState(() => {
    try {
      const saved = localStorage.getItem("mapLayerSettings");
      const settings = saved ? JSON.parse(saved) : {};

      return settings?.mapaAmbiental?.mapaLayer ?? 1;
    } catch (e) {
      return 1;
    }
  });
  const [clusterEnabled, setClusterEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem("mapLayerSettings");
      const settings = saved ? JSON.parse(saved) : {};

      return settings?.mapaAmbiental?.agrupamento === 1;
    } catch (e) {
      return false;
    }
  });
  const [filterToggle, setFilterToggle] = useState(false)
  const [enabledFilters, setEnabledFilters] = useState([])
  const [showMarkerTooltip, setShowMarkerTooltip] = useState(() => {
    try {
      const saved = localStorage.getItem("mapLayerSettings");
      const settings = saved ? JSON.parse(saved) : {};

      return settings?.mapaAmbiental?.showMarkerTooltip ?? true;
    } catch (e) {
      return true;
    }
  })
  const [filterSearchValue, setFilterSearchValue] = useState("")

  useEffect(() => {
    if (filters?.length) {
      setEnabledFilters(filters.map(filter => filter.tipo))
    }
  }, [filters])

  useEffect(() => {
    if (!focusItemId || !locations.length) return;

    const item = locations.find(location => location.id === focusItemId);
    if (!item) return;

    mapRef.current?.setView(item.position, 15);
    setTimeout(() => {
      history.replace({ pathname: location.pathname, state: {} });
    }, 50);
  }, [focusItemId, locations]);

  useEffect(() => {
    const saved = localStorage.getItem("mapLayerSettings");
    const currentSettings = saved ? JSON.parse(saved) : {};

    const updatedSettings = {
      ...currentSettings,
      mapaAmbiental: {
        ...(currentSettings.mapaAmbiental ?? {}),
        mapaLayer: layerType,
        agrupamento: clusterEnabled ? 1 : 0
      }
    };

    localStorage.setItem(
      "mapLayerSettings",
      JSON.stringify(updatedSettings)
    );
  }, [layerType, clusterEnabled]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("mapLayerSettings");
      const currentSettings = saved ? JSON.parse(saved) : {};

      const updatedSettings = {
        ...currentSettings,
        mapaAmbiental: {
          ...(currentSettings.mapaAmbiental ?? {}),
          showMarkerTooltip
        }
      };

      localStorage.setItem(
        "mapLayerSettings",
        JSON.stringify(updatedSettings)
      );
    } catch (e) {
      console.error(e);
    }
  }, [showMarkerTooltip]);

  const layerUrl = useMemo(() => {
    return layerType === 1
      ? "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      : "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
  }, [layerType])

  const markersToRender = useMemo(() => {
    return locations.filter(location => enabledFilters.includes(location.tipoMonitorado))
  }, [locations, enabledFilters])

  const toggleFilter = (tipo) => {
    setEnabledFilters(prev => {
      const alreadyEnabled = prev.includes(tipo)
      return alreadyEnabled ? prev.filter(t => t !== tipo) : [...prev, tipo]
    })
  }

  const renderMarkers = () => {
    return markersToRender.map((location) => (
      <MapMarker
        key={`${location.id}_${showMarkerTooltip}`}
        location={location}
        handlePopupOpen={handlePopupOpen}
        handlePopupClose={handlePopupClose}
        showMarkerTooltip={showMarkerTooltip}
        isActive={focusItemId === location.id}
      />
    ))
  }

  const renderLocationsList = () => {
    return filters
      .filter(filter => filter.tipo.toLowerCase().includes(filterSearchValue))
      .map((filter) => {
        const isEnabled = enabledFilters.includes(filter.tipo)

        return (
          <li
            key={filter.tipo}
            className={`${styles.markerToggleFilterListItem} ${!isEnabled ? styles.markerToggleFilterListItemDisabled : ""}`}
          >
            <label className={styles.markerToggleFilterListItemLabel}>
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={() => toggleFilter(filter.tipo)}
              />

              <div className={styles.markerToggleFilterListItemLabelWraper}>
                <div className={styles.markerToggleFilterListItemLabelIcon} style={{ backgroundColor: filter.cor }}>
                  <i className={filter.icone} />
                </div>
                <span>{filter.tipo}</span>
              </div>
              <i className={isEnabled ? "fa fa-eye fa-20px" : "fa fa-eye-slash fa-20px"} style={{ color: filter.cor }} />
            </label>
          </li>
        )
      })
  }

  const filterMarkers = (e) => {
    setFilterSearchValue(e.target.value.toLowerCase())
  }

  return (
    <>
      <MapContainer
        center={[-15.77972, -47.92972]}
        zoom={4}
        className={styles.map}
        ref={mapRef}
      >
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url={layerUrl}
        />

        {!clusterEnabled ? (
          renderMarkers()
        ) : (
          <MarkerClusterGroup
            showCoverageOnHover={false}
            maxClusterRadius={40}
          >
            {renderMarkers()}
          </MarkerClusterGroup>
        )}
      </MapContainer>

      <div
        className={styles.mapSearchButton}
        onClick={() => setFilterToggle(prev => !prev)}
      >
        <i
          style={{ transform: "scale(1)", width: "100%", height: "100%" }}
          className={filterToggle ? "fa fa-times fa-2x" : "fa fa-filter fa-2x"}
        />
      </div>

      {filterToggle && (
        <div className={styles.markerToggleFilter}>
          <div className={`marker-toggle-list ${styles.markerToggleFilterList}`}>
            <div className="d-flex align-items-center">
              <button
                style={{
                  padding: "4px 5px",
                  marginTop: "7px",
                  marginLeft: "8px",
                  borderRadius: "8px"
                }}
                onClick={() => {
                  setFilterToggle(false)
                  setFilterSearchValue("")
                }}
              >
                <i className="fa fa-times fa-2x" />
              </button>
              <input value={filterSearchValue} type="text" placeholder="Pesquisar" onChange={filterMarkers} />
            </div>
            <ul className={styles.markerToggleFilterListItens}>{renderLocationsList()}</ul>
          </div>
        </div>
      )}

      <div className={styles.showMarkerTooltipControls}>
        <button
          className={`${styles.groupControlButton} ${showMarkerTooltip ? styles.active : ""}`}
          onClick={() => setShowMarkerTooltip(true)}
        >
          Exibir resumos
        </button>

        <button
          className={`${styles.groupControlButton} ${!showMarkerTooltip ? styles.active : ""}`}
          onClick={() => setShowMarkerTooltip(false)}
        >
          Ocultar resumos
        </button>
      </div>

      <div className={styles.mapLayerControls}>
        <button
          className={layerType === 1 ? styles.active : ""}
          onClick={() => setLayerType(1)}
        >
          Mapa
        </button>

        <button
          className={layerType === 2 ? styles.active : ""}
          onClick={() => setLayerType(2)}
        >
          Satélite
        </button>
      </div>

      <div className={styles.mapGroupControls}>
        <button
          className={`${styles.groupControlButton} ${!clusterEnabled ? styles.active : ""}`}
          onClick={() => setClusterEnabled(false)}
        >
          Desagrupar
        </button>

        <button
          className={`${styles.groupControlButton} ${clusterEnabled ? styles.active : ""}`}
          onClick={() => setClusterEnabled(true)}
        >
          Agrupar
        </button>
      </div>
    </>
  )
}

export default Map